import type { FC } from 'react'
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import type { AnimationItem } from 'lottie-web'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useLottie from '@segment/utils/useLottie/useLottie'

import useIsInView from '@segment/utils/useInView'

import jsonUrl from './assets/the-omnichannel-success.json.data'

const HEADLINE = `Omnichannel success requires quality as much as quantity.`
const description = (
  <>
    <CopyText.Description tag="p" variant="pLarge">
      As well as adding more channels, businesses are concentrating on reducing fragmentation between the channels
      they’ve already invested in. Consumers agree. Given a choice, 61% say they’d prefer fewer, better channels when
      shopping with a brand.
    </CopyText.Description>
    <CopyText.Description tag="p" variant="h5">
      What customers say:
    </CopyText.Description>
    <CopyText.Description tag="p" variant="pMedium">
      “Segment has become the data hub that we needed. There isn’t a question on how or when data will get from one
      place to another. We feel confident we can execute communications and insights in the right way with the right
      data.”
    </CopyText.Description>
    <CopyText.Description tag="p" variant="h6">
      Ann Ehnert
    </CopyText.Description>
    <CopyText.Description tag="p" variant="textTiny">
      Manager of CRM & Loyalty Marketing at Schnuck Markets
    </CopyText.Description>
  </>
)
const link = {
  href: `/customers/schnucks/?utm_medium=referral&utm_source=segment&utm_campaign=nam_cnt_state-of-personalization_&utm_content=case-study`,
  text: `Read the full story`,
}

const OmnichannelSuccess: FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])

  return (
    <SectionBase variant="2Col" reverse={true}>
      <Section.Contents alignSelf="center">
        <Animation ref={containerRef} />
      </Section.Contents>
      <Section.Copy alignSelf="center">
        <CopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {HEADLINE}
          </CopyText.Heading>
          {description}
          <CopyText.Link size="medium" href={link.href}>
            {link.text}
          </CopyText.Link>
        </CopyTextBase>
      </Section.Copy>
    </SectionBase>
  )
}

export default OmnichannelSuccess

const Animation = styled.div`
  max-width: 566px;
  margin: 0 auto;
  aspect-ratio: 566 / 639;
`
